/**
 * Utility functions for exporting data to various formats.
 */

/**
 * Generates a standard filename based on the page name and current timestamp.
 * Format: page-name-DD-MMM-YYYY-HH-MM-AM/PM.csv
 * @param {string} pageName - The name of the page/module (e.g., 'job-posts')
 * @returns {string} - The formatted filename
 */
export const generateExportFilename = (pageName) => {
    const now = new Date();

    // Day: 06
    const day = now.getDate().toString().padStart(2, '0');

    // Month: Feb
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[now.getMonth()];

    // Year: 2026
    const year = now.getFullYear();

    // Time components
    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const hoursStr = hours.toString().padStart(2, '0');

    const timestamp = `${day}-${month}-${year}-${hoursStr}-${minutes}-${ampm}`;
    return `${pageName}-${timestamp}.csv`;
};

/**
 * Converts an array of objects to a CSV string with metadata headers.
 * @param {Array} data - Array of objects to convert.
 * @param {Object} options - Export options for metadata.
 * @param {string} options.title - Title/Name of the export.
 * @param {string} options.type - Type of export (Selected, Full Page, Date Range, All Data).
 * @param {Array} headers - Optional array of field names/headers.
 * @returns {string} - CSV string with metadata.
 */
export const convertToCSV = (data, options = {}, headers = null) => {
    if (!data || data.length === 0) return '';

    const now = new Date();
    const dateStr = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

    const csvRows = [];
    const actualHeaders = headers || Object.keys(data[0]);

    // Add Data Headers
    csvRows.push(actualHeaders.join(','));

    // Add data rows
    data.forEach((row) => {
        const values = actualHeaders.map((header) => {
            const value = row[header] !== undefined && row[header] !== null ? row[header] : '';
            // Escape double quotes and wrap in double quotes
            return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
    });

    // Metadata at the BOTTOM
    csvRows.push(''); // Blank line separator before metadata
    if (options.title) csvRows.push(`"Export Name:","${options.title}"`);
    if (options.type) csvRows.push(`"Export Type:","${options.type}"`);
    csvRows.push(`"Total Records:","${data.length}"`);
    csvRows.push(`"Export Date:","${dateStr}"`);
    csvRows.push(`"Export Time:","${timeStr}"`);

    return csvRows.join('\n');
};

/**
 * Initiates a browser download for a CSV file.
 * @param {string} csvContent - The CSV content string.
 * @param {string} filename - The name of the file to be downloaded.
 */
export const downloadCSVFile = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Downloads a file from the backend API.
 * @param {string} endpoint - The API endpoint URL.
 * @param {string} filename - The filename to save the downloaded file as.
 */
export const downloadFromBackend = async (endpoint, filename = 'export.csv', additionalHeaders = {}) => {
    try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                ...additionalHeaders
            }
        });

        if (!response.ok) {
            throw new Error('Failed to download file from backend');
        }

        const blob = await response.blob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Backend download error:', error);
        throw error;
    }
};

/**
 * Formats and downloads data as CSV with metadata and standardized filename.
 * @param {Array} data - Array of objects.
 * @param {string} pageName - Name of the page for filename.
 * @param {Object} options - Export metadata options (title, type).
 * @param {Array} headers - Optional headers.
 */
export const exportToCSV = (data, pageName, options = {}, headers = null) => {
    const filename = generateExportFilename(pageName);
    const csvContent = convertToCSV(data, options, headers);
    downloadCSVFile(csvContent, filename);
};
